// /src/context/TenantContext.tsx

'use client';

import { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { usePathname } from 'next/navigation';
import axios from 'axios';
import { getToken } from '@/lib/auth';
import { buildStrapiApiUrl } from '@/lib/strapiApi';
import { withPanelStrapiConcurrency } from '@/lib/panelStrapiConcurrency';
import { tenantSchema } from '@/lib/tenantSchema';
import { Tenant } from '@/types/tenant';

interface TenantMeta {
  attributes: Record<string, {
    type: string;
    enum?: string[];
    label?: string;
    // ... другие свойства схемы
  }>;
}

// src/context/TenantContext.tsx
interface TenantContextType {
  tenants: Tenant[];
  tenant: Tenant | null;   // ← именно так
  activeTenantId: number | null;
  tenantMeta: TenantMeta | null;
  loading: boolean;
  isUpdatingTenant: boolean;
  isSuperAdmin: boolean;
  refreshTenant: () => Promise<void>;
  updateTenantFields: (fields: Record<string, any>) => Promise<void>;
  handleSelectTenant: (tenantId: number) => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

type TenantBundle = {
  tenants: Tenant[];
  tenant: Tenant | null;
  activeTenantId: number | null;
  isSuperAdmin: boolean;
};

const TENANT_BUNDLE_CACHE_KEY = 'gild-panel-tenant-bundle-v3';
const TENANT_BUNDLE_CACHE_TTL_MS = 5 * 60 * 1000;

const readCachedTenantBundle = (): TenantBundle | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(TENANT_BUNDLE_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { savedAt: number; bundle: TenantBundle };
    if (Date.now() - parsed.savedAt > TENANT_BUNDLE_CACHE_TTL_MS) {
      sessionStorage.removeItem(TENANT_BUNDLE_CACHE_KEY);
      return null;
    }
    return parsed.bundle;
  } catch {
    return null;
  }
};

const writeCachedTenantBundle = (bundle: TenantBundle) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(
      TENANT_BUNDLE_CACHE_KEY,
      JSON.stringify({ savedAt: Date.now(), bundle }),
    );
  } catch {
    // ignore quota / private mode
  }
};

export const TenantProvider = ({ children }: { children: ReactNode }) => {
  const pathname = usePathname();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [activeTenantId, setActiveTenantId] = useState<number | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [tenantMeta, setTenantMeta] = useState<TenantMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [isUpdatingTenant, setIsUpdatingTenant] = useState(false);
  const hasLoadedTenantRef = useRef(false);
  const autoActiveTenantEnsuredRef = useRef<number | null>(null);
  const fetchIdRef = useRef(0);

  const applyTenantBundle = (fetchId: number, bundle: TenantBundle) => {
    if (fetchId !== fetchIdRef.current) {
      return;
    }

    unstable_batchedUpdates(() => {
      setTenants(bundle.tenants);
      setActiveTenantId(bundle.activeTenantId);
      setTenant(bundle.tenant);
      setIsSuperAdmin(bundle.isSuperAdmin === true);
      hasLoadedTenantRef.current = bundle.tenants.length > 0;
    });
  };

  const resolveTenantBundle = (
    tenantList: Tenant[],
    profileTenants: Tenant[],
    userProfile: Record<string, unknown> | null,
  ): TenantBundle | null => {
    // Сначала profile (users/me), затем полный my-tenant — чтобы не потерять поля вроде tagYandex.
    const allTenants = mergeTenantsById(profileTenants, tenantList);
    if (!allTenants.length) {
      return null;
    }

    const profileActiveId = extractActiveTenantId(userProfile);
    const activeFromProfile = profileActiveId
      ? allTenants.find(({ id }) => id === profileActiveId) ?? null
      : null;
    const activeFromMyTenant = tenantList[0] ?? null;

    const resolvedActiveId =
      activeFromProfile?.id ?? activeFromMyTenant?.id ?? allTenants[0].id;

    const orderedTenants = orderTenantsWithActiveFirst(allTenants, resolvedActiveId);
    const resolvedTenant =
      orderedTenants.find(({ id }) => id === resolvedActiveId) ?? orderedTenants[0];

    return {
      tenants: orderedTenants,
      activeTenantId: resolvedActiveId,
      tenant: resolvedTenant,
      isSuperAdmin: (userProfile as { superadmin?: boolean } | null)?.superadmin === true,
    };
  };

  const mergeTenantsById = (...lists: Tenant[][]): Tenant[] => {
    const map = new Map<number, Tenant>();
    lists.flat().forEach((item) => {
      const existing = map.get(item.id);
      // Поздний список перекрывает ключи, но не затирает объект целиком:
      // users/me часто отдаёт урезанный tenant без tagYandex/metrika и т.п.
      map.set(item.id, existing ? { ...existing, ...item } : item);
    });
    return Array.from(map.values());
  };

  const extractActiveTenantId = (profile: Record<string, unknown> | null): number | null => {
    if (!profile) return null;

    const rawActiveTenant = profile.activeTenant;
    if (rawActiveTenant && typeof rawActiveTenant === 'object') {
      const record = rawActiveTenant as Record<string, unknown>;
      const nestedId =
        record.id ??
        (record.data && typeof record.data === 'object'
          ? (record.data as Record<string, unknown>).id
          : undefined);
      const parsed = Number(nestedId);
      return Number.isFinite(parsed) ? parsed : null;
    }

    const parsed = Number(rawActiveTenant);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const orderTenantsWithActiveFirst = (list: Tenant[], activeId: number | null): Tenant[] => {
    if (activeId == null) return list;
    const active = list.find(({ id }) => id === activeId);
    if (!active) return list;
    return [active, ...list.filter(({ id }) => id !== activeId)];
  };

  const ensureActiveTenantOnServer = async (token: string, tenantId: number) => {
    if (autoActiveTenantEnsuredRef.current === tenantId) {
      return;
    }

    try {
      await axios.post(
        buildStrapiApiUrl('/api/my-tenant/active'),
        { tenantId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      autoActiveTenantEnsuredRef.current = tenantId;
    } catch (error) {
      autoActiveTenantEnsuredRef.current = null;
      throw error;
    }
  };

  const isLikelyTenant = (entity: Tenant) => {
    const candidateKeys: (keyof Tenant)[] = ['domain', 'companyName', 'city', 'region', 'slug'];
    return candidateKeys.some((key) => {
      const value = entity[key];
      if (typeof value === 'string') {
        return value.trim().length > 0;
      }
      return Boolean(value);
    });
  };

  const normalizeTenant = (rawTenant: unknown): Tenant | null => {
    if (!rawTenant || typeof rawTenant !== 'object') {
      return null;
    }

    const tenantRecord = rawTenant as Record<string, unknown>;
    const rawId = tenantRecord.id;
    if (rawId === undefined || rawId === null) {
      return null;
    }

    const resolvedId = typeof rawId === 'number' ? rawId : Number(rawId);
    if (Number.isNaN(resolvedId)) {
      return null;
    }

    if (tenantRecord.attributes && typeof tenantRecord.attributes === 'object') {
      const attributes = tenantRecord.attributes as Record<string, unknown>;
      return {
        ...attributes,
        id: resolvedId,
      } as Tenant;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { attributes, ...restTenant } = tenantRecord;
    return {
      ...restTenant,
      id: resolvedId,
    } as Tenant;
  };

  const extractTenants = (payload: unknown): Tenant[] => {
    const tenantMap = new Map<number, Tenant>();

    const collectTenants = (input: unknown) => {
      if (!input) {
        return;
      }

      if (Array.isArray(input)) {
        input.forEach((item) => collectTenants(item));
        return;
      }

      if (typeof input !== 'object') {
        return;
      }

      const record = input as Record<string, unknown>;

      const attributes = record.attributes;
      if (attributes && typeof attributes === 'object') {
        const attributesRecord = attributes as Record<string, unknown>;
        if (attributesRecord.tenants !== undefined) {
          collectTenants(attributesRecord.tenants);
        }
      }

      const nestedKeys: (keyof typeof record)[] = ['tenants', 'tenant', 'data', 'result'];
      nestedKeys.forEach((key) => {
        if (record[key] !== undefined) {
          collectTenants(record[key]);
        }
      });

      if (record.id !== undefined) {
        const normalizedTenant = normalizeTenant(record);
        if (normalizedTenant && isLikelyTenant(normalizedTenant)) {
          tenantMap.set(normalizedTenant.id, normalizedTenant);
        }
      }
    };

    collectTenants(payload);

    return Array.from(tenantMap.values());
  };

  const fetchTenantsFromMyTenant = async (token: string) => {
    try {
      const tenantRes = await withPanelStrapiConcurrency(() =>
        axios.get(buildStrapiApiUrl('/api/my-tenant'), {
          headers: { Authorization: `Bearer ${token}` },
          params: { 'populate[tenants]': '*' },
        }),
      );
      return extractTenants(tenantRes.data);
    } catch (error) {
      // Если запрос с populate падает, пробуем без глубокого populate
      if (axios.isAxiosError(error) && (error.response?.status === 400 || error.response?.status === 500)) {
        console.warn("Deep populate failed for my-tenant, retrying with basic populate");
        const fallbackRes = await axios.get(
          buildStrapiApiUrl('/api/my-tenant'),
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { 'populate[tenants]': '*' },
          }
        );
        return extractTenants(fallbackRes.data);
      }
      throw error;
    }
  };

  const fetchUserProfile = async (token: string) => {
    // Сначала пробуем с базовым populate
    const basicPopulateParams = {
      'populate[tenants]': '*',
      'populate[activeTenant]': '*',
    };

    try {
      const response = await withPanelStrapiConcurrency(() =>
        axios.get(buildStrapiApiUrl('/api/users/me'), {
          headers: { Authorization: `Bearer ${token}` },
          params: basicPopulateParams,
        }),
      );

      return response.data;
    } catch (error) {
      // Если базовый populate тоже падает, пробуем без populate
      if (axios.isAxiosError(error) && (error.response?.status === 400 || error.response?.status === 500)) {
        console.warn("Populate failed, retrying user profile without populate params");
        try {
          const fallbackResponse = await axios.get(
            buildStrapiApiUrl('/api/users/me'),
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          return fallbackResponse.data;
        } catch (fallbackError) {
          console.error("Fallback fetch also failed:", fallbackError);
          throw fallbackError;
        }
      }

      throw error;
    }
  };

  const fetchTenantsFromUsersMe = async (token: string) => {
    try {
      // Пробуем с базовым populate
      const userRes = await axios.get(
        buildStrapiApiUrl('/api/users/me'),
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { 
            'populate[tenants]': '*',
          },
        }
      );
      return extractTenants(userRes.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return [];
      }
      // Если ошибка 400 или 500, пробуем без populate
      if (axios.isAxiosError(error) && (error.response?.status === 400 || error.response?.status === 500)) {
        console.warn("Populate failed for users/me in fetchTenantsFromUsersMe, retrying without populate");
        try {
          const fallbackRes = await axios.get(
          buildStrapiApiUrl('/api/users/me'),
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
          return extractTenants(fallbackRes.data);
        } catch (fallbackError) {
          if (axios.isAxiosError(fallbackError) && fallbackError.response?.status === 404) {
            return [];
          }
          throw fallbackError;
        }
      }
      throw error;
    }
  };

  const fetchTenantList = async (token: string) => {
    try {
      return await fetchTenantsFromMyTenant(token);
    } catch (primaryError) {
      if (axios.isAxiosError(primaryError) && primaryError.response?.status === 404) {
        try {
          return await fetchTenantsFromUsersMe(token);
        } catch (fallbackError) {
          console.error("Fallback tenants fetch failed", fallbackError);
          throw fallbackError;
        }
      }

      throw primaryError;
    }
  };

  const fetchTenant = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    const token = getToken();
    if (!token) {
      console.warn("No token found");
      hasLoadedTenantRef.current = false;
      if (fetchId === fetchIdRef.current) {
        applyTenantBundle(fetchId, { tenants: [], tenant: null, activeTenantId: null, isSuperAdmin: false });
        setLoading(false);
      }
      return;
    }

    try {
      const cachedBundle = readCachedTenantBundle();
      if (cachedBundle && !hasLoadedTenantRef.current) {
        applyTenantBundle(fetchId, cachedBundle);
        hasLoadedTenantRef.current = true;
        setLoading(false);
      } else if (!hasLoadedTenantRef.current) {
        setLoading(true);
      }

      let userProfile: Record<string, unknown> | null = null;
      let tenantList: Tenant[] = [];

      const [profileResult, tenantListResult] = await Promise.allSettled([
        fetchUserProfile(token),
        fetchTenantList(token),
      ]);

      if (fetchId !== fetchIdRef.current) {
        return;
      }

      if (profileResult.status === 'fulfilled') {
        userProfile = profileResult.value;
      } else {
        console.error('Failed to load user profile:', profileResult.reason);
      }

      if (tenantListResult.status === 'fulfilled') {
        tenantList = tenantListResult.value;
      } else {
        console.warn('fetchTenantList (my-tenant) failed', tenantListResult.reason);
      }

      const profileTenants = userProfile
        ? extractTenants(
            ((userProfile as { tenants?: unknown }).tenants as unknown) ?? (userProfile as unknown),
          )
        : [];

      if (!tenantList.length) {
        tenantList = profileTenants;
      }

      if (!tenantList.length) {
        try {
          tenantList = await fetchTenantsFromUsersMe(token);
        } catch (usersMeErr) {
          console.warn('fetchTenantsFromUsersMe failed', usersMeErr);
        }
      }

      if (fetchId !== fetchIdRef.current) {
        return;
      }

      const bundle = resolveTenantBundle(tenantList, profileTenants, userProfile);
      if (!bundle) {
        applyTenantBundle(fetchId, { tenants: [], tenant: null, activeTenantId: null, isSuperAdmin: false });
        return;
      }

      applyTenantBundle(fetchId, bundle);
      writeCachedTenantBundle(bundle);
      hasLoadedTenantRef.current = true;

      const profileActiveId = extractActiveTenantId(userProfile);
      const needsPersistActiveTenant =
        profileActiveId == null || profileActiveId !== bundle.activeTenantId;

      if (needsPersistActiveTenant && bundle.activeTenantId != null) {
        try {
          await ensureActiveTenantOnServer(token, bundle.activeTenantId);
        } catch (ensureError) {
          console.warn('Failed to auto-select active tenant', ensureError);
        }
      }
    } catch (err) {
      console.error("Error loading tenant or schema:", err);
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void fetchTenant();
    setTenantMeta(tenantSchema);
  }, [fetchTenant]);

  useEffect(() => {
    if (!pathname.startsWith('/admin')) return;
    if (!getToken()) return;
    if (tenants.length > 0 && activeTenantId != null) return;

    void fetchTenant();
  }, [pathname, tenants.length, activeTenantId, fetchTenant]);

  const updateTenantFields = async (fields: Record<string, any>) => {
    if (!tenant) {
      console.warn("updateTenantFields: no tenant");
      return;
    }

    if (isUpdatingTenant) {
      throw new Error('Дождитесь завершения переключения города перед сохранением');
    }

    const token = getToken();
    if (!token) {
      console.warn("No token found");
      return;
    }

    const tenantId = Number((tenant as { id?: number }).id);
    if (!Number.isFinite(tenantId) || tenantId <= 0) {
      throw new Error('Не удалось определить id города для сохранения');
    }

    // Явно фиксируем id из UI: сервер обновляет именно его, а не «ещё старый» activeTenant.
    const payload = { ...fields, tenantId };

    console.log('updateTenantFields: sending', JSON.stringify({ data: payload }, null, 2));

    try {
      const response = await axios.put(
        buildStrapiApiUrl('/api/my-tenant'),
        { data: payload },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('updateTenantFields: success', response.data);
      await fetchTenant();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('updateTenantFields: error', error.response?.status, error.response?.data);
        throw error;
      }
      throw error;
    }
  };

  const handleSelectTenant = async (tenantId: number) => {
    if (tenantId === activeTenantId) {
      return;
    }

    const selectedTenant = tenants.find(({ id }) => id === tenantId);
    if (!selectedTenant) {
      return;
    }

    const token = getToken();
    if (!token) {
      console.warn("No token found");
      return;
    }

    setIsUpdatingTenant(true);

    try {
      // Сначала фиксируем active на сервере, потом меняем UI.
      // Иначе форма уже показывает новый город, а PUT /my-tenant ещё пишет в старый.
      await axios.post(
        buildStrapiApiUrl('/api/my-tenant/active'),
        { tenantId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      autoActiveTenantEnsuredRef.current = tenantId;

      const reorderedTenants = [
        selectedTenant,
        ...tenants.filter(({ id }) => id !== tenantId),
      ];

      setTenants(reorderedTenants);
      setActiveTenantId(tenantId);
      setTenant(selectedTenant);
    } catch (error) {
      autoActiveTenantEnsuredRef.current = null;
      console.error("Failed to persist active tenant", error);
    } finally {
      setIsUpdatingTenant(false);
    }
  };

  return (
    <TenantContext.Provider
      value={{
        tenants,
        tenant,
        activeTenantId,
        tenantMeta,
        loading,
        isUpdatingTenant,
        isSuperAdmin,
        refreshTenant: fetchTenant,
        updateTenantFields,
        handleSelectTenant,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant должен использоваться внутри <TenantProvider>');
  return ctx;
};